/**
 * FileDropzone — the app's standardized file-upload control.
 *
 * One reusable component for every upload surface: drag-and-drop OR click to
 * browse, with built-in file-type + size validation, loading and selected
 * states. Styling uses the shared --ex-* design tokens (FileDropzone.css), so
 * it matches the brand everywhere and adapts to light/dark automatically.
 *
 * Usage:
 *   <FileDropzone
 *     accept=".txt,.md,.csv,.pdf,.docx"
 *     maxSizeMB={10}
 *     loading={isUploading}
 *     loadingLabel="Reading file…"
 *     selectedName={fileName}
 *     onFile={(file) => handleFile(file)}
 *     onError={(msg) => setError(msg)}
 *   />
 *   // Multi-file:
 *   <FileDropzone multiple onFiles={(files) => handleFiles(files)} ... />
 */
import React, { useCallback, useRef, useState } from 'react'
import './FileDropzone.css'

function extOf(name) {
  if (!name || !name.includes('.')) return ''
  return `.${name.split('.').pop().toLowerCase()}`
}

function parseAccept(accept) {
  return (accept || '')
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean)
}

export default function FileDropzone({
  accept = '',
  maxSizeMB = 10,
  disabled = false,
  loading = false,
  loadingLabel = 'Uploading…',
  selectedName = null,
  multiple = false,
  onFile,
  onFiles,
  onError,
  title = 'Drag & drop a file, or click to browse',
  hint,
  size = 'md', // 'md' | 'sm'
  className = '',
}) {
  const inputRef = useRef(null)
  const [dragOver, setDragOver] = useState(false)
  const acceptList = parseAccept(accept)
  const maxBytes = maxSizeMB ? maxSizeMB * 1024 * 1024 : 0
  const busy = disabled || loading

  const validate = useCallback(
    (file) => {
      if (!file) return 'No file selected.'
      if (acceptList.length) {
        const ext = extOf(file.name)
        const okExt = ext && acceptList.includes(ext)
        const okMime = acceptList.some((a) => a.includes('/') && file.type === a)
        if (!okExt && !okMime) {
          return `Unsupported file type. Allowed: ${acceptList.join(', ')}.`
        }
      }
      if (maxBytes && file.size > maxBytes) {
        return `That file is too large. Maximum size is ${maxSizeMB}MB.`
      }
      return null
    },
    [acceptList, maxBytes, maxSizeMB]
  )

  const handleFiles = useCallback(
    (fileList) => {
      const picked = Array.from(fileList || [])
      if (!picked.length) return

      const valid = []
      for (const file of picked) {
        const err = validate(file)
        if (err) {
          onError?.(err)
          if (!multiple) return
          continue
        }
        valid.push(file)
        if (!multiple) break
      }
      if (!valid.length) return

      if (multiple || onFiles) {
        onFiles?.(valid)
        if (!onFiles) {
          valid.forEach((file) => onFile?.(file))
        }
        return
      }
      onFile?.(valid[0])
    },
    [validate, onFile, onFiles, onError, multiple]
  )

  const openPicker = () => {
    if (!busy) inputRef.current?.click()
  }

  const onDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    if (busy) return
    handleFiles(e.dataTransfer.files)
  }

  const hintText =
    hint ||
    [
      acceptList.length ? acceptList.join(', ') : '',
      maxSizeMB ? `up to ${maxSizeMB}MB` : '',
      multiple ? 'multiple files OK' : '',
    ]
      .filter(Boolean)
      .join(' · ')

  const defaultTitle = multiple
    ? 'Drag & drop files, or click to browse'
    : title

  return (
    <div
      className={[
        'fdz',
        `fdz--${size}`,
        dragOver ? 'fdz--drag' : '',
        busy ? 'fdz--busy' : '',
        selectedName ? 'fdz--has-file' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      role="button"
      tabIndex={busy ? -1 : 0}
      aria-disabled={busy}
      aria-label={selectedName ? `File selected: ${selectedName}. Click to replace.` : defaultTitle}
      onClick={openPicker}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !busy) {
          e.preventDefault()
          openPicker()
        }
      }}
      onDrop={onDrop}
      onDragOver={(e) => {
        e.preventDefault()
        if (!busy) setDragOver(true)
      }}
      onDragLeave={(e) => {
        e.preventDefault()
        setDragOver(false)
      }}
    >
      <input
        ref={inputRef}
        type="file"
        className="fdz-input"
        accept={accept}
        multiple={multiple || undefined}
        disabled={busy}
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          handleFiles(e.target.files)
          e.target.value = ''
        }}
      />

      <span className="fdz-icon" aria-hidden="true">
        {loading ? (
          <span className="fdz-spinner" />
        ) : selectedName ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <path d="M9 15l2 2 4-4" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
        )}
      </span>

      <span className="fdz-text">
        <span className="fdz-title">
          {loading ? loadingLabel : selectedName || defaultTitle}
        </span>
        <span className="fdz-hint">
          {loading
            ? 'Hang tight…'
            : selectedName
              ? (multiple ? 'Click or drop to add more files' : 'Click or drop a file to replace')
              : hintText}
        </span>
      </span>
    </div>
  )
}
